import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import ChildTabSelector from './ChildTabSelector';
import AttendanceDetailView from './AttendanceDetailView';
import './ParentAttendanceView.css';
import './ParentDetailPages.css';

function ParentAttendanceView({ children }) {
  const [selectedChild, setSelectedChild] = useState(null);
  const [student, setStudent] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleChildSelect = (child) => {
    setSelectedChild(child);
  };

  useEffect(() => {
    if (!selectedChild && children.length > 0) {
      setSelectedChild(children[0]);
    }
  }, [children, selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      loadAttendanceData(selectedChild);
    }
  }, [selectedChild]);

  const loadAttendanceData = async (child) => {
    try {
      setLoading(true);
      setStudent(null);
      setClassInfo(null);
      setAttendanceRecords([]);

      // Use the child data directly - it already has id, studentId, classId, fullName, etc.
      const studentData = {
        id: child.id,
        studentId: child.studentId,
        classId: child.classId,
        fullName: child.fullName || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'Unknown',
        firstName: child.firstName,
        lastName: child.lastName,
      };

      setStudent(studentData);
      console.log('🔥 ATTENDANCE: Using child data directly:', studentData.fullName);

      // Load class info
      if (studentData.classId) {
        try {
          const classData = await classInfoService.getClassInfo(studentData.classId);
          setClassInfo(classData);
        } catch (error) {
          console.log('🔥 ATTENDANCE: Error loading class info:', error);
          setClassInfo({
            id: studentData.classId,
            name: 'Class ' + studentData.classId.slice(-8),
            grade: 'N/A',
            source: 'error'
          });
        }
      }

      // Load attendance records from classes/{classId}/attendanceRecords
      const attendanceData = [];
      if (studentData.classId) {
        try {
          const attendanceSubcollection = collection(db, 'classes', studentData.classId, 'attendanceRecords');

          // Try with child's UUID (document ID) first - this is what iOS uses
          if (child.id) {
            const q1 = query(attendanceSubcollection, where('studentId', '==', child.id));
            const snap1 = await getDocs(q1);
            snap1.forEach(doc => {
              attendanceData.push({ id: doc.id, ...doc.data() });
            });
          }

          // If no results, try with the studentId
          if (attendanceData.length === 0 && child.studentId) {
            const q2 = query(attendanceSubcollection, where('studentId', '==', child.studentId));
            const snap2 = await getDocs(q2);
            snap2.forEach(doc => {
              attendanceData.push({ id: doc.id, ...doc.data() });
            });
          }

          console.log('🔥 ATTENDANCE: Loaded', attendanceData.length, 'records for', studentData.fullName);
        } catch (error) {
          console.error('🔥 ATTENDANCE: Error loading attendance:', error);
        }
      }

      setAttendanceRecords(attendanceData);
    } catch (error) {
      console.error('Error loading attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStats = () => {
    const totalRecords = attendanceRecords.length;

    const presentCount = attendanceRecords.filter(record => record.status?.toLowerCase() === 'present').length;
    const absentCount = attendanceRecords.filter(record => record.status?.toLowerCase() === 'absent').length;
    const lateCount = attendanceRecords.filter(record => record.status?.toLowerCase() === 'late').length;
    const excusedCount = attendanceRecords.filter(record => record.status?.toLowerCase() === 'excused').length;
    const attendanceRate = totalRecords > 0 ? Math.round(((presentCount + excusedCount) / totalRecords) * 100) : 0;

    const stats = { presentCount, absentCount, lateCount, excusedCount, attendanceRate };
    return stats;
  };

  const stats = getAttendanceStats();

  if (loading) {
    return (
      <div className="loading-container">
      </div>
    );
  }

  return (
    <div className="attendance-wrapper">
      <ChildTabSelector children={children} onChildSelect={handleChildSelect} />
      
      {selectedChild && (
        <div className="attendance-container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading attendance...</p>
            </div>
          ) : !student ? (
            <div className="empty-state">
              <p>Student information not found</p>
            </div>
          ) : (
            <>
              <div className="student-header">
                <h2>{student.fullName}</h2>
                <p>Student ID: {student.studentId}</p>
                <p>Class: {classInfo?.name || 'Not assigned'} ({classInfo?.grade || 'N/A'})</p>
              </div>

              <div className="attendance-stats">
                <h3>Attendance Overview</h3>
                <div className="stats-grid">
                  <div className="stat-card present">
                    <div className="stat-value">{stats.presentCount}</div>
                    <div className="stat-label">Present</div>
                  </div>
                  <div className="stat-card absent">
                    <div className="stat-value">{stats.absentCount}</div>
                    <div className="stat-label">Absent</div>
                  </div>
                  <div className="stat-card late">
                    <div className="stat-value">{stats.lateCount}</div>
                    <div className="stat-label">Late</div>
                  </div>
                  <div className="stat-card excused">
                    <div className="stat-value">{stats.excusedCount}</div>
                    <div className="stat-label">Excused</div>
                  </div>
                  <div className="stat-card rate">
                    <div className="stat-value">{stats.attendanceRate}%</div>
                    <div className="stat-label">Attendance Rate</div>
                  </div>
                </div>
              </div>

              <div className="attendance-records">
                <h3>Attendance Records</h3>
                
                {console.log('🔥 ATTENDANCE: Rendering records, count:', attendanceRecords.length)}
                {console.log('🔥 ATTENDANCE: Records data:', attendanceRecords)}
                
                {attendanceRecords.length === 0 ? (
                  <div className="empty-state">
                    <p>No attendance records available</p>
                    <p className="empty-subtitle">Attendance records will appear here once teachers mark attendance</p>
                  </div>
                ) : (
                  <div className="records-list">
                    {attendanceRecords
                      .sort((a, b) => {
                        const dateA = a.date?.toDate?.() ? new Date(a.date.toDate()) : new Date(a.date);
                        const dateB = b.date?.toDate?.() ? new Date(b.date.toDate()) : new Date(b.date);
                        return dateB - dateA;
                      })
                      .map((record, index) => (
                        <div 
                          key={index} 
                          className="attendance-record clickable" 
                          onClick={() => setSelectedRecord(record)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="record-date">
                            {record.date?.toDate?.() ? 
                              new Date(record.date.toDate()).toLocaleDateString() : 
                              new Date(record.date).toLocaleDateString()}
                          </div>
                          <div className="record-status">
                            <span className={`status-badge ${record.status}`}>
                              {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                            </span>
                          </div>
                          {record.notes && (
                            <div className="record-comments">
                              <small>{record.notes}</small>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {selectedRecord && (
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => setSelectedRecord(null)}>
            ◄ Back to Attendance
          </button>
          <AttendanceDetailView
            attendanceRecord={selectedRecord}
            student={{ ...student, classInfo }}
            onClose={() => setSelectedRecord(null)}
          />
        </div>
      )}
    </div>
  );
}

export default ParentAttendanceView;
